import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import * as moment from 'moment';

import { Project, ProjectService } from '@app/providers/projects';
import { NotificationMessageService } from '@app/shared/notification-message/notification-message.service';
import {
  Pod,
  ProjectOverwriteServices,
  OverwriteService,
} from './dynamic-environment-api.model';
import { AuthService } from '@app/providers/auth/auth.service';
import { ConfigManagerV2Service } from '@app/config-manager/shared/api-v2.service';
import {
  GonfigManagerEnvironment,
  GonfigManagerCreateEnvironmentRequest,
  GonfigManagerUpdateEnvironmentRequest,
} from '@app/config-manager/shared/api-v2.model';

@Injectable()
export class DynamicEnvironmentService {
  private project: Project;

  static parseData(environmentForm: UntypedFormGroup): any {
    const data = environmentForm.value;
    data.expiration = moment(environmentForm.get('expiration').value).unix();

    // Remove property if is null
    if (!data.linkage) {
      delete data.linkage;
    }

    return data;
  }

  constructor(
    private service: ProjectService,
    private message: NotificationMessageService,
    private auth: AuthService,
    private configManagerV2: ConfigManagerV2Service,
  ) {
    this.service.project$.subscribe(
      (project: Project) => (this.project = project),
    );
  }

  private handleError<T>(operation = 'operation', result?: T) {
    return ({ error }: { error: any }): Observable<T> => {
      console.error(error);
      this.message.error(
        error,
        'Server not responding.\nRunning Charles? Quit & try again\nUsing Adblock? Disable & try again\nNone of the above? Contact us on our slack channel: #spt-support-backend',
      );
      return of(result as T);
    };
  }

  getEnvironment(identifier: string): Observable<Pod[]> {
    return this.configManagerV2.getEnvironment(identifier).pipe(
      map((env) => [toPod(env)]),
      catchError(this.handleError<Pod[]>('getEnvironment', [])),
    );
  }

  getEnvironments(): Observable<Pod[]> {
    return this.configManagerV2.listEnvironments().pipe(
      map((envs) => envs.map(toPod)),
      catchError(this.handleError('getEnvironments', [])),
    );
  }

  deleteEnvironment(identifier: string): Observable<{}> {
    return this.configManagerV2
      .deleteEnvironment(identifier)
      .pipe(catchError(this.handleError('deleteEnvironment')));
  }

  addEnvironment(data: any): Observable<{}> {
    const body = toCreateRequest(data);
    return this.configManagerV2
      .createEnvironment(body)
      .pipe(catchError(this.handleError('addEnvironment')));
  }

  editEnvironment(data: any, identifier: string): Observable<{}> {
    const body = toUpdateRequest(data, this.auth.profile?.email);
    return this.configManagerV2
      .updateEnvironment(identifier, body)
      .pipe(catchError(this.handleError('editEnvironment')));
  }

  getBranches(): Observable<string[]> {
    return this.configManagerV2
      .listBackendBranches()
      .pipe(catchError(this.handleError('getBranches', [])));
  }

  getProjectData(data: any): Observable<any> {
    const dynenvChart = this.project.config.dynenvChart;
    if (!dynenvChart) return of(data);

    return of(
      Object.assign(
        {},
        data,
        { testenv: true, chart: dynenvChart },
        { chart_version: `0.0.0-${this.dirifyBranch(data.branch)}` },
      ),
    );
  }

  getOverwriteServices(): OverwriteService[] {
    if (ProjectOverwriteServices.has(this.project.short_name)) {
      return ProjectOverwriteServices.get(this.project.short_name);
    }

    return [];
  }

  private dirifyBranch(branch: string): string {
    return branch.replace(/[^-0-9a-z]/gi, '-');
  }
}

function toPod(env: GonfigManagerEnvironment): Pod {
  return {
    ...env,
    is_production: String(env.is_production),
  };
}

function toCreateRequest(data: any): GonfigManagerCreateEnvironmentRequest {
  const overwrite = Object.fromEntries(
    Object.entries(data.overwrite ?? {}).filter(
      ([, v]) => v !== null && v !== undefined && v !== '',
    ),
  ) as Record<string, string>;

  return {
    identifier: data.identifier,
    branch: data.branch,
    sync_backend: data.sync_backend === true,
    user: data.user,
    facebook_app_id: data.facebook_app_id,
    statics_server: data.statics_server,
    localizations_server: data.localizations_server,
    is_production: data.is_production === true,
    expiration: data.expiration,
    cmbranch: data.cmbranch || data.config_branch || undefined,
    chart: data.chart || undefined,
    chart_version: data.chart_version || undefined,
    facebook_app_secret: data.facebook_app_secret || undefined,
    facebook_app_namespace: data.facebook_app_namespace || undefined,
    linkage: data.linkage || undefined,
    overwrite: Object.keys(overwrite).length > 0 ? overwrite : undefined,
  };
}

function toUpdateRequest(
  data: any,
  email: string,
): GonfigManagerUpdateEnvironmentRequest {
  return {
    user: email,
    branch: data.branch,
    cmbranch: data.cmbranch,
    expiration: data.expiration,
  };
}
